import { graphql } from '~/graphql';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import { useGraphqlMutation } from '~/hooks/useGraphqlMutation';
import { UserStatus } from '~/graphql/graphql';
import type { MsrBuyerCodeCandidatesQuery } from '~/graphql/graphql';

const tenantUsersQuery = graphql(`
  query tenantUsers {
    users {
      id
      externalId
      firstName
      lastName
      employeeId
      phoneNumber
      role
      status
      lastEditedAt
      lastEditedBy {
        id
        firstName
        lastName
      }
    }
  }
`);

const organizationBuyersQuery = graphql(`
  query organizationBuyers {
    users: buyListTeamCandidates {
      id
      firstName
      lastName
      status
    }
  }
`);

const createTenantUserMutation = graphql(`
  mutation createTenantUser($input: CreateTenantUserInput!) {
    createTenantUser(input: $input) {
      id
    }
  }
`);

const updateTenantUserMutation = graphql(`
  mutation updateTenantUser($userId: ID!, $input: UpdateTenantUserInput!) {
    updateTenantUser(userId: $userId, input: $input) {
      id
    }
  }
`);

const setTenantUserRoleMutation = graphql(`
  mutation setTenantUserRole($userId: ID!, $role: UserRole!) {
    setTenantUserRole(userId: $userId, role: $role) {
      id
    }
  }
`);

const changeTenantUserTradeRoleMutation = graphql(`
  mutation changeTenantUserTradeRole($userId: ID!, $role: UserRole!) {
    changeTenantUserTradeRole(userId: $userId, role: $role) {
      id
      role
    }
  }
`);

const deactivateTenantUserMutation = graphql(`
  mutation deactivateTenantUser($userId: ID!) {
    deactivateTenantUser(userId: $userId) {
      id
    }
  }
`);

const reactivateTenantUserMutation = graphql(`
  mutation reactivateTenantUser($userId: ID!) {
    reactivateTenantUser(userId: $userId) {
      id
    }
  }
`);

const resendTenantUserInviteMutation = graphql(`
  mutation resendTenantUserInvite($userId: ID!) {
    resendTenantUserInvite(userId: $userId) {
      id
    }
  }
`);

export const useTenantUsers = () =>
  useGraphqlQuery(tenantUsersQuery, ['tenantUsers']);

export const useGetOrganizationBuyers = (enabled = true) =>
  useGraphqlQuery(organizationBuyersQuery, ['organizationBuyers'], undefined, {
    enabled,
  });

export const useCreateTenantUser = () =>
  useGraphqlMutation(createTenantUserMutation, [
    'tenantUsers',
    'organizationBuyers',
  ]);

export const useUpdateTenantUser = () =>
  useGraphqlMutation(updateTenantUserMutation, [
    'tenantUsers',
    'organizationBuyers',
  ]);

export const useSetTenantUserRole = () =>
  useGraphqlMutation(setTenantUserRoleMutation, [
    'tenantUsers',
    'organizationBuyers',
  ]);

export const useChangeTenantUserTradeRole = () =>
  useGraphqlMutation(changeTenantUserTradeRoleMutation, [
    'tenantUsers',
    'organizationBuyers',
  ]);

export const useDeactivateTenantUser = () =>
  useGraphqlMutation(deactivateTenantUserMutation, [
    'tenantUsers',
    'organizationBuyers',
  ]);

export const useReactivateTenantUser = () =>
  useGraphqlMutation(reactivateTenantUserMutation, [
    'tenantUsers',
    'organizationBuyers',
  ]);

export const useResendTenantUserInvite = () =>
  useGraphqlMutation(resendTenantUserInviteMutation, ['tenantUsers']);

const msrBuyerCodeCandidatesQuery = graphql(`
  query MsrBuyerCodeCandidates($organizationId: ID!) {
    organization(id: $organizationId) {
      id
      users {
        id
        firstName
        lastName
        employeeId
        status
      }
    }
  }
`);

type CandidateUser = NonNullable<
  MsrBuyerCodeCandidatesQuery['organization']
>['users'][number];

export type MsrBuyerCodeCandidate = Pick<
  CandidateUser,
  'id' | 'firstName' | 'lastName'
> & { employeeId: string };

/**
 * Active or invited users with an MSR employee ID, one per ID. When users
 * share an ID, the current user is the one kept, so their own code still
 * defaults to them.
 */
export function toBuyerCodeCandidates(
  users: readonly CandidateUser[],
  currentUserId?: string
): MsrBuyerCodeCandidate[] {
  const byEmployeeId = new Map<string, MsrBuyerCodeCandidate>();
  for (const user of users) {
    const employeeId = (user.employeeId ?? '').trim();
    const eligible =
      user.status === UserStatus.Active || user.status === UserStatus.Invited;
    const taken = byEmployeeId.has(employeeId);
    if (
      eligible &&
      employeeId !== '' &&
      (!taken || user.id === currentUserId)
    ) {
      byEmployeeId.set(employeeId, {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        employeeId,
      });
    }
  }
  return [...byEmployeeId.values()].sort(
    (a, b) =>
      a.lastName.localeCompare(b.lastName) ||
      a.firstName.localeCompare(b.firstName)
  );
}

export const useMsrBuyerCodeCandidates = (
  organizationId: string | undefined,
  currentUserId: string | undefined,
  enabled: boolean
) => {
  const query = useGraphqlQuery(
    msrBuyerCodeCandidatesQuery,
    ['msrBuyerCodeCandidates', organizationId],
    { organizationId: organizationId ?? '' },
    { enabled: enabled && organizationId != null }
  );
  return {
    candidates: toBuyerCodeCandidates(
      query.data?.organization?.users ?? [],
      currentUserId
    ),
    isLoading: query.isLoading,
    isError: query.isError,
  };
};
